'use client'

import { Fragment } from 'react'
import { useTranslations } from 'next-intl'

import {
  Button,
  NotificationCard as DesignSystemNotificationCard,
  NotificationEmphasis,
  NotificationMedium,
} from '@graftcode/design-system'

import { NOTIFICATION_DRAWER_TEST_IDS } from '@common/common-test-ids.const'

import { getNotificationIconConfig } from '../../mappers/getNotificationIconConfig'
import type { Notification } from '../../notification-drawer.type'

type Props = {
  notification: Notification
  onAction?: (notificationId: string, actionId: string) => void
}

export function NotificationCard({ notification, onAction }: Props) {
  const t = useTranslations('notifications')
  const { icon, badge } = getNotificationIconConfig(notification.type)
  const actions = notification.actions ?? []

  return (
    <div data-testid={NOTIFICATION_DRAWER_TEST_IDS.card}>
      <DesignSystemNotificationCard
        item={{
          id: notification.id,
          title: t(notification.titleKey),
          icon,
          badge,
          unread: !notification.isRead,
          timestamp: notification.timestamp,
          description: notification.description.map((segment) => (
            <Fragment key={`${notification.id}-${segment.text}`}>
              {segment.emphasis ? (
                <NotificationEmphasis>{segment.text}</NotificationEmphasis>
              ) : segment.medium ? (
                <NotificationMedium>{segment.text}</NotificationMedium>
              ) : (
                segment.text
              )}
              {segment.breakAfter ? <br /> : null}
            </Fragment>
          )),
          actions:
            actions.length > 0
              ? actions.map((action) => (
                  <Button
                    key={action.id}
                    size="m"
                    weight="semibold"
                    variant={action.variant === 'primary' ? 'primary' : 'outlined'}
                    onClick={() => onAction?.(notification.id, action.id)}
                  >
                    {t(action.labelKey)}
                  </Button>
                ))
              : undefined,
        }}
      />
    </div>
  )
}
